import { useState, useCallback, useRef, useEffect } from "react";
import { postDraftFormData } from "../services/formService";
import { Logger } from "../utils/logger";

interface UseDraftSavingProps {
  formUniqueCode: string | undefined;
  formData: any;
  formStructure?: any[]; // The form.form array with field definitions (array of questions)
  debounceMs?: number;
}

interface UseDraftSavingReturn {
  saveDraft: (isSubmitted?: boolean) => Promise<void>;
  debouncedSaveDraft: (isSubmitted?: boolean) => void;
  isSaving: boolean;
  lastSavedAt: Date | null;
  draftResponseId: string | null;
}

/**
 * Custom hook for managing draft save functionality on form navigation
 *
 * Features:
 * - Saves draft when user clicks "Next" button (via navigation hook)
 * - Persistent draft tracking using _id from API response
 * - First save creates a new draft, subsequent saves update the same draft
 * - Stores draft response ID in localStorage for recovery
 * - Silent error handling (non-critical feature)
 * - Cleanup of pending timeouts on unmount
 * - Filters out empty values and end_screen
 *
 * Workflow:
 * 1. First draft save: POST to /draft-response without _id → API returns new _id
 * 2. Subsequent saves: POST to /draft-response with _id → API updates existing draft
 * 3. Draft response ID is persisted in localStorage and state
 * 4. On successful form submission, draft is marked as submitted and localStorage is cleaned
 *
 * @param formUniqueCode - Unique identifier for the form
 * @param formData - Current form data object with all field values
 * @param debounceMs - Debounce delay in milliseconds (default: 1000ms, currently unused but kept for future use)
 *
 * @example
 * const { saveDraft, draftResponseId } = useDraftSaving({
 *   formUniqueCode: formData.uniqueCode,
 *   formData: newFormData,
 *   debounceMs: 1000
 * });
 *
 * // Pass saveDraft to navigation hook - it will be called on "Next" button click
 * const { validateConditions } = useFormNavigation({
 *   formData,
 *   newFormData,
 *   onSubmit: submitForm,
 *   onFileUpload: getSignUrl,
 *   onSaveDraft: saveDraft // Called automatically when user clicks Next
 * });
 *
 * // Mark as submitted after final submission
 * await submitForm();
 * await saveDraft(true); // isSubmitted = true
 */
export const useDraftSaving = ({
  formUniqueCode,
  formData,
  formStructure,
  debounceMs = 1000
}: UseDraftSavingProps): UseDraftSavingReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [draftResponseId, setDraftResponseId] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved draft response ID from localStorage on mount
  useEffect(() => {
    if (formUniqueCode) {
      const savedDraftId = localStorage.getItem(`draft_response_id_${formUniqueCode}`);
      if (savedDraftId) {
        setDraftResponseId(savedDraftId);
        Logger.log("Loaded existing draft response ID from localStorage", { draftResponseId: savedDraftId });
      }
    }
  }, [formUniqueCode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Save draft immediately (no debounce)
   */
  const saveDraft = useCallback(async (isSubmitted: boolean = false) => {
    if (!formUniqueCode) {
      Logger.log("Draft save skipped: No formUniqueCode provided");
      return;
    }

    try {
      setIsSaving(true);

      // Filter out end_screen and empty values
      const { end_screen, ...data } = formData;
      const filteredData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== "")
      );

      // Only save if there's actual data
      if (Object.keys(filteredData).length === 0) {
        Logger.log("Draft save skipped: No data to save");
        return;
      }

      // Nest group fields by question and group field name
      const structuredData: any = {};

      if (formStructure && formStructure.length > 0) {
        formStructure.forEach((question: any, questionIndex: number) => {
          const questionKey = `question_${questionIndex + 1}`;

          question.fields.forEach((field: any) => {
            if (field.type === 'group_field' && field.groupFields) {
              // This is a group field - nest it
              const groupData: any = {};

              field.groupFields.forEach((nestedField: any) => {
                if (filteredData[nestedField.fieldName] !== undefined) {
                  groupData[nestedField.fieldName] = filteredData[nestedField.fieldName];
                }
              });

              // Only add if group has data
              if (Object.keys(groupData).length > 0) {
                if (!structuredData[questionKey]) {
                  structuredData[questionKey] = {};
                }
                structuredData[questionKey][field.fieldName] = groupData;
              }
            } else {
              // Regular field - keep at root level (flat)
              if (filteredData[field.fieldName] !== undefined) {
                structuredData[field.fieldName] = filteredData[field.fieldName];
              }
            }
          });
        });

        // Add any remaining fields not in structure (for safety)
        Object.keys(filteredData).forEach((key) => {
          const isInStructure = formStructure.some((q: any) =>
            q.fields.some((f: any) => {
              if (f.fieldName === key) return true;
              if (f.type === 'group_field' && f.groupFields) {
                return f.groupFields.some((gf: any) => gf.fieldName === key);
              }
              return false;
            })
          );

          if (!isInStructure && !structuredData[key]) {
            structuredData[key] = filteredData[key];
          }
        });
      } else {
        // No form structure - fall back to flat structure
        Object.assign(structuredData, filteredData);
      }

      Logger.log("Draft data (nested structure):", structuredData);

      // Call API with _id if we have one (for updates), otherwise create new draft
      const response = await postDraftFormData(
        formUniqueCode,
        structuredData,
        isSubmitted,
        draftResponseId || undefined
      );

      // Extract and save the _id from the response
      if (response?.data?.data?._id) {
        const newDraftId = response.data.data._id;

        // Only update if it's a new ID
        if (newDraftId !== draftResponseId) {
          setDraftResponseId(newDraftId);
          localStorage.setItem(`draft_response_id_${formUniqueCode}`, newDraftId);
          Logger.log("Draft response ID saved", { draftResponseId: newDraftId });
        }
      }

      setLastSavedAt(new Date());
      Logger.log("Draft saved successfully", {
        uniqueCode: formUniqueCode,
        isSubmitted,
        draftResponseId: draftResponseId || 'new',
        structure: structuredData
      });
    } catch (err: any) {
      // Silently fail - draft is insurance, shouldn't break the form
      Logger.log("Draft save failed (non-critical)", {
        error: err?.message,
        status: err?.response?.status
      });
    } finally {
      setIsSaving(false);
    }
  }, [formUniqueCode, formData, draftResponseId]);

  /**
   * Save draft with debouncing (waits for user to stop typing)
   */
  const debouncedSaveDraft = useCallback((isSubmitted: boolean = false) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout to save draft after inactivity
    timeoutRef.current = setTimeout(() => {
      saveDraft(isSubmitted);
    }, debounceMs);
  }, [saveDraft, debounceMs]);

  return {
    saveDraft,
    debouncedSaveDraft,
    isSaving,
    lastSavedAt,
    draftResponseId
  };
};
